import { Account } from '@radixdlt/radix-dapp-toolkit';
import { Link, Outlet, useFetcher, useLoaderData, useRevalidator } from "react-router";
import { MyLiquidity } from '~/components/liquidity-my';
import AccountSelector from '~/components/account-selector';
import { action as lpAction } from '~/routes/liquidity._index';
import { loader as walletLoader } from '~/routes/wallet.$account';
import TokenInput from '~/components/token-input';
import TokenRow from '~/components/token-row';
import TransactionButton from '~/components/transaction-button';
import { useEffect, useState } from 'react';
import { classNames } from '~/lib/util';
import { LoaderFunctionArgs, redirect } from 'react-router';
import { getPair, getPairByBaseToken } from '~/lib/models/pair';
import { getToken } from '~/lib/models/tokens';
import { useRadix } from '~/rdt/radixProvider';
import { generateRemoveLiquidityManifest } from '~/lib/manifests';
import { getLPToken } from '~/lib/models/lpToken';
import { fetchPairState, fetchTokensDetails } from '~/rdt/gateway';
import { setDbFromContext } from '~/lib/db';
import { trackEvent } from "~/lib/analytics";

export async function loader({ params, context }: LoaderFunctionArgs) {
  setDbFromContext(context);

  if (!params.componentAddress) {
    throw redirect('/liquidity/add');
  }

  // check if we have a component address or the old basetoken address
  const componentAddress = params.componentAddress;

  let row;

  if (componentAddress.startsWith('component_')) {
    const [pairRow] = await getPair(componentAddress);
    row = pairRow;
  } else {
    const [pairRow] = await getPairByBaseToken(componentAddress);
    row = pairRow;
  }

  const pair = row.Pair;
  let lpAddress;
  let poolState;

  const { pairState, basePoolState, quotePoolState } = await fetchPairState(pair);

  if (params.direction == "quote") {
    poolState = quotePoolState;
    lpAddress = pair.quoteLPToken;
  } else {
    poolState = basePoolState;
    lpAddress = pair.baseLPToken;
  }

  const [lpToken] = await getLPToken(lpAddress);
  const [baseToken] = await getToken(pair.baseToken);
  const [quoteToken] = await getToken(pair.quoteToken);

  const [lpState] = await fetchTokensDetails([lpAddress]);

  return ({
    isBase: params.direction == "base",
    pair,
    pairState,
    poolState,
    lpToken,
    baseToken,
    quoteToken,
    lpState,
  });
}

export default function LiquidityRemove() {
  const { isBase, pair, pairState, poolState, lpToken, baseToken, quoteToken, lpState } = useLoaderData<typeof loader>();

  const fetcher = useFetcher<typeof lpAction>();
  const walletFetcher = useFetcher<typeof walletLoader>();
  const revalidator = useRevalidator();
  const { sendTransaction } = useRadix();

  const [account, setAccount] = useState<Account | null>(null);
  const [inputAmount, setInputAmount] = useState<string>("");
  const [baseAmount, setBaseAmount] = useState<string>("");
  const [quoteAmount, setQuoteAmount] = useState<string>("");

  useEffect(() => {
    setInputAmount("");
  }, [poolState]);

  useEffect(() => {
    const share = +inputAmount / (lpState.totalSupply || 1);
    const baseIndex = isBase ? 0 : 1;
    const quoteIndex = isBase ? 1 : 0;

    setBaseAmount("" + share * poolState?.vaults[baseIndex].amount);
    setQuoteAmount("" + share * poolState?.vaults[quoteIndex].amount);
  }, [inputAmount]);

  function onAccount(account: Account) {
    setAccount(account);

    fetcher.submit(
      { account: account.address },
      {
        action: "/liquidity/my?index",
        method: "POST",
      },
    );

    walletFetcher.load(`/wallet/${account.address}`);
  }

  function setMax() {
    setInputAmount(walletFetcher.data?.filter((b) => b.address == lpToken.address)[0]?.amount);
  }

  function onRemoveLiquidity() {
    if (!account) {
      return;
    }

    if (!pair) {
      return;
    }

    const transactionManifest = generateRemoveLiquidityManifest(account.address, lpToken.address, inputAmount, !isBase, pair);

    sendTransaction(transactionManifest, `Remove ${lpToken.symbol} from DefiPlaza`);

    trackEvent("send_tx", {
      type: "removeLiquidity",
    });

    revalidator.revalidate();
    setInputAmount("");
    walletFetcher.load(`/wallet/${account.address}`);
  }

  return (
    <div className="mx-4 mb-8 xl:container xl:mx-auto">
      <div className="lg:items-top flex flex-col-reverse lg:flex-row lg:space-x-4">
        <MyLiquidity pairs={fetcher.data?.pairs || []} />

        <div className="flex flex-col space-y-4 lg:w-1/2">
          <h2 className="mx-4 mt-8 text-2xl font-semibold lg:hidden">Remove Liquidity</h2>

          <AccountSelector mode="light" onAccount={onAccount}></AccountSelector>

          <section className="flex flex-col rounded-xl bg-white p-4 lg:rounded-2xl lg:p-8">
            <div className="flex items-center justify-between">
              <label htmlFor="tokenInput" className="text-sm text-purple-800">
                Removing
              </label>
              {walletFetcher.data ? (
                <span className="w-60 truncate text-right text-sm font-normal text-black lg:text-left">
                  Balance: {Math.round((walletFetcher.data.filter((b) => b.address == lpToken.address)[0]?.amount || 0) * 1000000) / 1000000}
                </span>
              ) : null}
            </div>
            <div className="flex items-center justify-between">
              <TokenInput value={inputAmount} onChange={setInputAmount} />

              <button onClick={setMax} className="border-primary-500 text-primary-500 hover:bg-primary-500 rounded-full border px-3 py-1 text-xs hover:text-white sm:text-sm lg:mr-4">
                MAX
              </button>

              <TokenRow token={lpToken} />
            </div>
          </section>

          <p className="flex justify-between px-8">
            <svg className="h-6 w-6" viewBox="8 8 24 24" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M29.088 21.972c.432-.443.432-1.212 0-1.654a1.128 1.128 0 0 0-1.617 0l-6.322 6.466V10.58c0-.645-.513-1.17-1.144-1.17-.63 0-1.143.525-1.143 1.17v16.203l-6.321-6.466a1.128 1.128 0 0 0-1.618 0 1.178 1.178 0 0 0-.335.827c0 .312.12.606.336.827l7.964 8.143a1.556 1.556 0 0 0 2.237 0l7.963-8.143z"
                fill="currentColor"
                fillRule="nonzero"
              ></path>
            </svg>
          </p>

          <section className="flex flex-col rounded-xl bg-white p-4 lg:rounded-2xl lg:p-8">
            <label htmlFor="outputAmount" className="text-sm text-purple-800">
              Returned Base Tokens
            </label>
            <div className="flex flex-wrap items-center justify-between">
              <input
                type="number"
                inputMode="decimal"
                id="outputAmount"
                name="outputAmount"
                value={baseAmount}
                placeholder="0.00"
                autoComplete="off"
                spellCheck="false"
                disabled={true}
                className={classNames(
                  // navigation.state == 'submitting' ? 'animate-pulse bg-gray-200  rounded-sm' : 'bg-transparent',
                  "mr-4 w-1/2 flex-auto border-none p-0 text-4xl text-black placeholder:text-purple-100 lg:w-60 dark:placeholder:text-purple-800/80",
                )}
              />

              <TokenRow token={baseToken} />
            </div>
          </section>

          <section className="flex flex-col rounded-xl bg-white p-4 lg:rounded-2xl lg:p-8">
            <label htmlFor="outputAmount" className="text-sm text-purple-800">
              Returned Quote Tokens
            </label>
            <div className="flex flex-wrap items-center justify-between">
              <input
                type="number"
                inputMode="decimal"
                id="outputAmount"
                name="outputAmount"
                value={quoteAmount}
                placeholder="0.00"
                autoComplete="off"
                spellCheck="false"
                disabled={true}
                className={classNames(
                  // navigation.state == 'submitting' ? 'animate-pulse bg-gray-200  rounded-sm' : 'bg-transparent',
                  "mr-4 w-1/2 flex-auto border-none p-0 text-4xl text-black placeholder:text-purple-100 lg:w-60 dark:placeholder:text-purple-800/80",
                )}
              />

              <TokenRow token={quoteToken} />
            </div>
          </section>

          <TransactionButton title="Remove Liquidity" onClick={onRemoveLiquidity} />
        </div>
      </div>
    </div>
  );
}
